'use client';

import { useEffect, useState } from 'react';

import { buildEmbedCode } from './embed/buildEmbedCode';
import type { CategorySelectField } from './fields';
import { useCopyToClipboard } from './hooks/useCopyToClipboard';
import { WidgetPreview } from './preview/WidgetPreview';
import type { WidgetSchema } from './types';
import { WidgetWorkbench } from './WidgetWorkbench';

/** One category example: a live ticker + a short, one-line iframe snippet. */
function CategoryExample({ schema, category, origin }: { schema: WidgetSchema; category: string; origin: string }) {
    const { copy, copied } = useCopyToClipboard();
    // Compact single-line snippet: drop the HTML comment markers and collapse whitespace.
    const code = buildEmbedCode(schema, { category }, 'iframe', origin)
        .replace(/<!--.*?-->/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    return (
        <div className="rounded-x40 border border-stroke-x30 bg-surface-x10 p-5 shadow-x10">
            <div className="mb-3 flex items-center justify-between gap-3">
                <p className="typ-emphasis-x30 text-text-x10">{category}</p>
                <button
                    type="button"
                    onClick={() => copy(code)}
                    className="inline-flex h-8 shrink-0 items-center rounded-full bg-text-x10 px-4 text-xs font-medium text-white transition-opacity hover:opacity-85"
                >
                    {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            {/* The live banner is the same edge-to-edge embed a partner gets; a soft shadow
                just lifts it off the card. Lazy so off-screen examples don't open a socket. */}
            <div className="overflow-hidden rounded-x30 shadow-x10">
                <WidgetPreview schema={schema} config={{ category }} />
            </div>
            <code className="mt-3 block overflow-x-auto whitespace-nowrap rounded-x30 border border-stroke-x30 bg-[#f5f5f3] px-3 py-2 font-mono text-[12px] text-text-x10">
                {code}
            </code>
        </div>
    );
}

/**
 * Ticker-tape page: a showcase of one ready-to-paste example per category (one per
 * row), with a "configure your own" workbench below for full customization.
 * Categories are read from the schema's category-select field so the two never drift.
 */
export function TickerTapeShowcase({ schema }: { schema: WidgetSchema }) {
    const [origin, setOrigin] = useState('https://YOUR_WIDGETS_HOST');
    useEffect(() => {
        setOrigin(window.location.origin);
    }, []);

    const categoryField = schema.fields.find((field) => field.kind === 'category-select') as
        | CategorySelectField
        | undefined;
    const categories = categoryField?.options ?? [];

    return (
        <div className="space-y-12">
            <section>
                <p className="typ-overline-x10 mb-1 text-text-x30">Examples by category</p>
                <p className="typ-body-x20 mb-5 max-w-xl text-text-x20">
                    Ready to paste — copy the snippet for any category. Each shows live markets and updates on its own.
                </p>
                <div className="space-y-4">
                    {categories.map((c) => (
                        <CategoryExample key={c.value} schema={schema} category={c.value} origin={origin} />
                    ))}
                </div>
            </section>

            <section>
                <p className="typ-overline-x10 mb-1 text-text-x30">Want something custom?</p>
                <p className="typ-body-x20 mb-6 max-w-xl text-text-x20">
                    Configure your own — choose the category, logo color, border, and corners, then grab the code.
                </p>
                <WidgetWorkbench schema={schema} />
            </section>
        </div>
    );
}
